import { domQuerySelector } from '@propertyfinder/pf-frontend-common/src/helper/dom/query-selector';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';

import { PropertyModel } from 'common/data/property/model';
import { PropertyCategoryIdentifierAdapter } from 'common/data/property-category/identifier/adapter';
import { DataStore } from 'common/module/data/store';
import { PropertyReportButtonTemplatePropsInterface } from 'common/module/property-report/button/template-props.interface';
import { StatsTealiumDataLayerEventReferenceEnum } from 'common/module/stats/tealium/data-layer/event-reference.enum';
import { StatsTealiumProviderInterface } from 'common/module/stats/tealium/provider.interface';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

import { PropertyReportReportFormInterface } from '../report-form.interface';
import { PropertyReportSettingsFormInterface } from '../settings-form.interface';
import { FormPropertyReportViewStore } from '../view-store';
import { FormPropertyReportViewStoreEvent } from '../view-store.event';

export class PropertyReportButtonViewStore extends DataStore {
  /**
   * property data to be used in the report modal
   */
  private property: PropertyModel;
  /**
   * @inheritDoc
   */
  protected state: PropertyReportButtonTemplatePropsInterface;

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private settings: PropertyReportSettingsFormInterface,
    private propertyReportFormViewDynamic: ViewDynamicInterface<FormPropertyReportViewStore>,
    private statsTealiumProviderService: StatsTealiumProviderInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private categoryIdentifier: PropertyCategoryIdentifierAdapter
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritdoc
   */
  public getState(): PropertyReportButtonTemplatePropsInterface {
    return this.state;
  }

  /**
   * @inheritDoc
   */
  public initialize(): void {
    this.state = {
      isLoggedIn: this.isUserLoggedIn(),
      onReportPropertyClick: this.onReportPropertyClick,
    };
    this.observers();
  }

  public isUserLoggedIn(): boolean {
    return !!this.userAuthenticationService?.getToken();
  }

  private observers(): void {
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInSucceeded, this.onAuthSuccess);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationSucceeded, this.onAuthSuccess);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.logOutSucceeded, this.onLogOutSucceeded);
  }

  private onAuthSuccess = (): void => {
    this.state.isLoggedIn = true;
    this.setState(this.state);
  };

  private onLogOutSucceeded = (): void => {
    if (!this.isUserLoggedIn()) {
      this.state.isLoggedIn = false;
      this.setState(this.state);
    }
  };

  private onReportPropertyClick = (): void => {
    if (this.isUserLoggedIn()) {
      this.openPropertyReportModal();
    }
  };
  /**
   *
   * Saves the property data to be used in the report modal
   */
  public setProperty(property: PropertyModel): void {
    this.property = property;
  }

  public openPropertyReportModal = (): void => {
    this.statsTealiumProviderService.setEventReference(StatsTealiumDataLayerEventReferenceEnum.REPORT_PROPERTY);

    this.openReportModal();
  };

  private openReportModal(): void {
    let form: PropertyReportReportFormInterface;

    if (this.settings.report.form) {
      form = this.settings.report.form;
    } else {
      form = this.categoryIdentifier.isRental(this.property.category_id)
        ? this.settings.report.rentForm
        : this.settings.report.buyForm;
    }

    this.propertyReportFormViewDynamic.getViewStore().initialize({
      model: this.property,
      reasons: form.report_property_form.reason_id.choices,
      reporter_type: form.report_property_form.reporter_type.choices,
      resetObservers: true,
    });

    this.propertyReportFormViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(FormPropertyReportViewStoreEvent.close, this.onClose);

    this.propertyReportFormViewDynamic.initialize({
      element: domQuerySelector(document, '[data-qs="dialog"]'),
    });

    this.propertyReportFormViewDynamic.render();
  }

  private destroyPropertyReportForm(): void {
    this.propertyReportFormViewDynamic
      .getViewStore()
      .getEventEmitter()
      .removeListener(FormPropertyReportViewStoreEvent.close, this.onClose);

    this.propertyReportFormViewDynamic.destroy();
  }

  /**
   * Clicked close on the property report form
   */
  private onClose = (): void => {
    this.destroyPropertyReportForm();
  };
}
